import React, { createContext, useContext, useEffect, useId, useState } from 'react';
import ReactDom from 'react-dom';
import { Platform, StyleSheet, View } from 'react-native';

type PortalType = {
    key: string;
    children: React.ReactNode;
};

type PortalContextType = {
    mount: (key: string, children: React.ReactNode) => void;
    unmount: (key: string) => void;
};

const PortalContext = createContext<PortalContextType | null>(null);

const PortalHost = ({ portals }: { portals: PortalType[] }) => {
    if (Platform.OS === 'web') return null;

    return (
        <View style={[StyleSheet.absoluteFill, { zIndex: 999999 }]} pointerEvents="box-none">
            {portals.map((p) => (
                <React.Fragment key={p.key}>{p.children}</React.Fragment>
            ))}
        </View>
    );
};

export const PortalProvider = ({ children }: { children: React.ReactNode }) => {
    const [portals, setPortals] = useState<PortalType[]>([]);

    const mount = (key: string, children: React.ReactNode) => {
        setPortals((prev) => {
            const existing = prev.find((p) => p.key === key);
            if (existing) {
                return prev.map((p) => (p.key === key ? { key, children } : p));
            }
            return [...prev, { key, children }];
        });
    };

    const unmount = (key: string) => {
        setPortals((prev) => prev.filter((p) => p.key !== key));
    };

    return (
        <PortalContext.Provider value={{ mount, unmount }}>
            <View style={{ flex: 1 }}>
                {children}
                <PortalHost portals={portals} />
            </View>
        </PortalContext.Provider>
    );
};

export const Portal = ({ children }: { children: React.ReactNode }) => {
    const context = useContext(PortalContext);
    const key = useId();

    // On Web, escape the entire React Native root to avoid clipping from parents with transform/overflow
    if (Platform.OS === 'web' && typeof document !== 'undefined') {
        return ReactDom.createPortal(
            <View
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2147483647,
                } as any}
                pointerEvents="box-none"
            >
                {children}
            </View>,
            document.body
        );
    }

    // On Native, use our context-based PortalHost
    useEffect(() => {
        if (context) {
            context.mount(key, children);
        }
        return () => {
            if (context) {
                context.unmount(key);
            }
        };
    }, [key, context, children]);

    return null;
};
